import { beginArticleWrite } from "@core/FileStructue/Article/pendingArticleWrite";
import AppBusy, { type BusyHold } from "@core-ui/AppBusy";
import { useDebounce } from "@core-ui/hooks/useDebounce";
import { traced } from "@ext/loggers/opentelemetry";
import { type MutableRefObject, useCallback, useEffect, useMemo, useRef } from "react";

/** A pending mark (see pendingArticleWrite.ts) that can follow the article to the path a rename gives it. */
interface PendingMark {
	moveTo(articlePath: string): void;
	release(): void;
}

const openPendingMark = (catalogName: string, articlePath: string, onRelease: () => void): PendingMark => {
	let release = beginArticleWrite(catalogName, articlePath);
	return {
		moveTo(to) {
			const releaseOld = release;
			release = beginArticleWrite(catalogName, to);
			releaseOld();
		},
		release() {
			onRelease();
			release();
		},
	};
};

/** A write already bound to its article: the caller closes over the context at edit time, because
 * by write time the user may have switched articles and a late binding would post to the new URL. */
type ArticleWrite = () => Promise<void>;

interface SaveArticle {
	/** Content edit: arms the debounce and marks the app busy until the write completes. */
	onContentChanged: (write: ArticleWrite) => void;
	/** Title edit in the first line — same cycle as content. */
	onTitleChanged: (write: ArticleWrite) => void;
	/** Rename on blur: the work is queued, the hold is taken immediately. */
	queueTitleRename: (write: ArticleWrite) => void;
	/** Write out everything pending and await writes in flight. Rejects if a write failed. */
	flush: () => Promise<void>;
}

interface SaveArticleProps {
	delayMs: number;
	/**
	 * Identity of the addressed article: changing it flushes pending work, as does unmounting.
	 * Compared with Object.is: a string or a memoized object, never an object created each render.
	 */
	articleKey: unknown;
	/**
	 * Same article `articleKey` addresses, as `parseContent` on the other end names it — see
	 * pendingArticleWrite.ts. Both together: the path alone repeats across catalogs.
	 */
	catalogName: string;
	articlePath: string;
}

/**
 * Ownership of the article autosave.
 *
 * One logical operation, one `save` hold: taken at edit time (an armed debounce is already unsaved
 * work) and released when the write is durably done. The same hold drives the on-screen indicator,
 * so "there is unsaved work" is visible to the user, not only to tests.
 */
export const useSaveArticle = ({ delayMs, articleKey, catalogName, articlePath }: SaveArticleProps): SaveArticle => {
	const contentHoldRef = useRef<BusyHold | null>(null);
	const titleHoldRef = useRef<BusyHold | null>(null);
	// Read fresh inside onContentChanged without forcing it to be recreated per render — the same
	// reason ArticleEditRenderer keeps its own props in refs.
	const articleRef = useRef({ articlePath, catalogName });
	articleRef.current = { articlePath, catalogName };
	// Released once the write that follows actually lands — see pendingArticleWrite.ts. Taken here,
	// not inside the write itself: a read racing in before the debounce even fires must already find
	// it, the same way an armed debounce already holds AppBusy busy before the request exists. One per field.
	const contentMarkRef = useRef<PendingMark | null>(null);
	const titleMarkRef = useRef<PendingMark | null>(null);
	// Handed-off marks too: a write already in flight still lands on the path the article has now.
	const openMarks = useRef(new Set<PendingMark>());
	// useDebounce does not expose its arguments, and flush needs to write the latest state.
	const pendingContentRef = useRef<ArticleWrite | null>(null);
	const pendingTitleRef = useRef<ArticleWrite | null>(null);
	// A task leaves this set only once it has been reported: successful ones at once, failed ones
	// when flush surfaces the error. Otherwise a write that failed before anyone asked would be
	// forgotten, and forceSave would report "saved" for an unsaved article.
	const inFlight = useRef(new Set<Promise<unknown>>());
	/**
	 * One queue per article, not per field.
	 *
	 * Content and props are different commands (`article/updateContent`, `item/updateProps`), but
	 * both rewrite the same file in full. Overlapping, each hands the disk its own view of the
	 * article and the one that started earlier overwrites the newer one — an edit disappearing
	 * without a trace. So the unit of ordering is the article: everything that rewrites it queues
	 * behind whatever is already writing.
	 */
	const writeChain = useRef(Promise.resolve());

	const own = useCallback((holdRef: MutableRefObject<BusyHold | null>, work: ArticleWrite) => {
		const hold = holdRef.current;
		holdRef.current = null;
		const task: Promise<void> = writeChain.current.then(work).then(
			() => {
				hold?.release();
				inFlight.current.delete(task);
			},
			(error: unknown) => {
				hold?.release();
				throw error;
			},
		);
		writeChain.current = task.catch(() => {});
		inFlight.current.add(task);
		// Nobody awaits this task in the common case (the debounce fires it), so without this branch
		// a failed write becomes an unhandled rejection. The error is not hidden: it stays in
		// inFlight until flush reports it.
		void task.catch(() => {});
		return task;
	}, []);

	const runContentSave = useCallback(
		(write: ArticleWrite) => {
			pendingContentRef.current = null;
			// Handoff, same as contentHoldRef: an edit arriving after this point (the write already
			// dispatched, not merely armed) is a new burst and has to open its own pending mark, not
			// reuse this one — see the "second edit while the first write is still in flight" test.
			contentMarkRef.current = null;
			return own(contentHoldRef, write);
		},
		[own],
	);

	const runTitleSave = useCallback(
		(write: ArticleWrite) => {
			pendingTitleRef.current = null;
			// Handoff, as in runContentSave.
			titleMarkRef.current = null;
			return own(titleHoldRef, write);
		},
		[own],
	);

	const { start: startContentSave, cancel: cancelContentSave } = useDebounce(runContentSave, delayMs);
	const { start: startTitleSave, cancel: cancelTitleSave } = useDebounce(runTitleSave, delayMs);

	/**
	 * Keeps a read of the article waiting until this edit's write lands. Title edits too: a read between
	 * the content write and the title write queued after it came back with the old title.
	 */
	const markPending = useCallback((markRef: MutableRefObject<PendingMark | null>, write: ArticleWrite) => {
		if (!markRef.current) {
			const { catalogName, articlePath } = articleRef.current;
			const mark = openPendingMark(catalogName, articlePath, () => openMarks.current.delete(mark));
			openMarks.current.add(mark);
			markRef.current = mark;
		}
		// Captured now, not read back from the ref inside the closure below: the run clears the ref at
		// handoff so the *next* burst can open its own mark, and by the time this write actually settles
		// the ref may already hold (or have released) a different burst's mark.
		const mark = markRef.current;

		// Whichever keystroke's write actually runs — the debounce's last one, or flush's early one — is
		// the one that releases the mark this burst opened (or joined, if one was already open).
		const tracked: ArticleWrite = async () => {
			try {
				await write();
			} finally {
				mark.release();
			}
		};
		return tracked;
	}, []);

	// A rename keeps the editor and its key and changes the path. The write goes to the new path once the
	// rename settles, so the marks move there too: a read of the renamed article must wait for it as well.
	const addressRef = useRef({ articleKey, articlePath });
	useEffect(() => {
		const before = addressRef.current;
		addressRef.current = { articleKey, articlePath };
		if (before.articleKey !== articleKey || before.articlePath === articlePath) return;
		for (const mark of openMarks.current) mark.moveTo(articlePath);
	}, [articleKey, articlePath]);

	const onContentChanged = useCallback(
		(write: ArticleWrite) => {
			contentHoldRef.current ??= AppBusy.hold("save");
			const tracked = markPending(contentMarkRef, write);
			pendingContentRef.current = tracked;
			startContentSave(tracked);
		},
		[markPending, startContentSave],
	);

	const onTitleChanged = useCallback(
		(write: ArticleWrite) => {
			titleHoldRef.current ??= AppBusy.hold("save");
			const tracked = markPending(titleMarkRef, write);
			pendingTitleRef.current = tracked;
			startTitleSave(tracked);
		},
		[markPending, startTitleSave],
	);

	const queueTitleRename = useCallback((work: ArticleWrite) => {
		// The hold is taken synchronously, not inside the link: while the previous link is in flight
		// the work is already inevitable, but the counter would not know about it yet.
		const hold = AppBusy.hold("save");
		const chained: Promise<void> = writeChain.current.then(work).then(
			() => {
				hold.release();
				inFlight.current.delete(chained);
			},
			(error: unknown) => {
				hold.release();
				throw error;
			},
		);

		inFlight.current.add(chained);
		// This catch both keeps the queue going after a failed link and silences the unhandled
		// rejection; the error itself stays in inFlight until it is reported.
		writeChain.current = chained.catch(() => {});
	}, []);

	// Flushes rather than cancels: an unfired debounce holds the last milliseconds of typing, which
	// switching articles would otherwise drop. Write errors propagate — a forceSave that reports
	// "saved" for a failed request is worse than no forceSave at all.
	const flush = useCallback(async (): Promise<void> => {
		const tasks: Promise<unknown>[] = [...inFlight.current];

		const content = pendingContentRef.current;
		if (content) {
			cancelContentSave();
			tasks.push(runContentSave(content));
		}

		const title = pendingTitleRef.current;
		if (title) {
			cancelTitleSave();
			tasks.push(runTitleSave(title));
		}

		const results = await Promise.allSettled(tasks);

		// These are reported here and can be forgotten. Failures that started after the snapshot stay
		// in the set and belong to the next flush.
		for (const task of tasks) inFlight.current.delete(task);

		const rejected = results.find((result) => result.status === "rejected");
		if (rejected) throw (rejected as PromiseRejectedResult).reason;
	}, [cancelContentSave, cancelTitleSave, runContentSave, runTitleSave]);

	// articleKey is unused in the body — it is the trigger: changing it runs the cleanup, which
	// flushes pending work before the article switches.
	// biome-ignore lint/correctness/useExhaustiveDependencies: see the comment above
	useEffect(() => {
		return () => {
			// The component is gone, so there is nobody to show the error to: traced records it on the
			// span and the catch keeps it from becoming an unhandled rejection.
			void traced("article-flush-pending-saves", flush).catch(() => {});
		};
	}, [articleKey, flush]);

	return useMemo(
		() => ({ onContentChanged, onTitleChanged, queueTitleRename, flush }),
		[onContentChanged, onTitleChanged, queueTitleRename, flush],
	);
};
